import type { BaseEntity, EntityServiceClient } from "@brains/plugins";
import { internalFullScope } from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import type { ImportResult, RawEntity } from "../types";
import type { FileOperations } from "./file-operations";
import type { Quarantine } from "./quarantine";
import type { ImageJobQueueDeps } from "./image-job-queue";
import {
  getImportContentSkipMessage,
  getImportContentSkipReason,
} from "./import-content-filter";
import { deserializeImportEntity } from "./import-deserialization";
import { queueImportImageConversions } from "./import-image-conversions";
import { getImportPathDecision } from "./import-path-filter";
import { persistImportEntity } from "./import-persistence";
import { OversizedFileError } from "./oversized-file-error";
import {
  createImportResult,
  logImportSummary,
  recordImportIssue,
  recordImportReadError,
  recordSkippedImport,
} from "./import-result";

export interface ImportPipelineDeps {
  entityService: EntityServiceClient;
  logger: Logger;
  fileOperations: FileOperations;
  quarantine: Quarantine;
  imageJobQueue: ImageJobQueueDeps;
  maxImportFileBytes: number;
  entityTypes?: string[] | undefined;
}

export async function importEntities(
  deps: ImportPipelineDeps,
  paths?: string[],
): Promise<ImportResult> {
  deps.logger.debug("Importing entities from directory");

  const result = createImportResult();

  const filesToProcess = paths ?? (await deps.fileOperations.getAllSyncFiles());

  for (const filePath of filesToProcess) {
    await importFile(deps, filePath, result);
  }

  logImportSummary(deps.logger, filesToProcess.length, result);
  return result;
}

async function importFile(
  deps: ImportPipelineDeps,
  filePath: string,
  result: ImportResult,
): Promise<void> {
  const pathDecision = getImportPathDecision(deps, filePath);
  if (pathDecision.skip) {
    if (pathDecision.countSkipped) {
      recordSkippedImport(result);
    }
    return;
  }

  try {
    const rawEntity = await deps.fileOperations.readEntity(
      filePath,
      deps.maxImportFileBytes,
    );

    await processEntityImport(deps, rawEntity, filePath, result);
  } catch (error) {
    if (error instanceof OversizedFileError) {
      recordSkippedImport(result);
      recordImportIssue(result, filePath, error.message);
      return;
    }
    recordImportReadError(deps.logger, filePath, error, result);
  }
}

async function processEntityImport(
  deps: ImportPipelineDeps,
  rawEntity: RawEntity,
  filePath: string,
  result: ImportResult,
): Promise<void> {
  const contentSkipReason = getImportContentSkipReason(rawEntity);
  if (contentSkipReason) {
    deps.logger.debug(getImportContentSkipMessage(contentSkipReason), {
      path: filePath,
      entityType: rawEntity.entityType,
    });
    recordSkippedImport(result);
    return;
  }

  const existing = await deps.entityService.getEntity({
    entityType: rawEntity.entityType,
    id: rawEntity.id,
    visibilityScope: internalFullScope(
      "directory sync indexes entities across all visibility tiers",
    ),
  });
  if (existing && canSkipBeforeDeserialization(deps, existing, rawEntity)) {
    recordSkippedImport(result);
    return;
  }

  queueImportImageConversions(deps.imageJobQueue, rawEntity, filePath);

  const parsedEntity = await deserializeImportEntity(
    deps,
    rawEntity,
    filePath,
    result,
  );
  if (!parsedEntity) {
    return;
  }

  await persistImportEntity(
    deps,
    rawEntity,
    parsedEntity,
    filePath,
    result,
    existing,
  );
}

function canSkipBeforeDeserialization(
  deps: ImportPipelineDeps,
  existing: BaseEntity,
  rawEntity: RawEntity,
): boolean {
  if (deps.fileOperations.shouldUpdateEntity(existing, rawEntity)) {
    return false;
  }

  return (
    rawEntity.entityType !== "document" ||
    Bun.deepEquals(existing.metadata, rawEntity.metadata ?? {})
  );
}
